import React, { useContext, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import useResource from "../hooks/resource";
import notificationApi from "../utils/api/notifications";
import appContext from "./AppContext";

const Context = React.createContext();

export const NotificationStore = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const client = useQueryClient();
  const appStore = useContext(appContext);
  const userId = appStore.get("user.id");
  const [notificationSettings, setNotificationSettings] = useState(
    appStore.get("user.notificationSettings"),
  );
  const [frequencySettings, setFrequencySettings] = useState(
    appStore.get("user.deliverySettings"),
  );
  // const [notifications, setNotifications] = useState([]);

  const notificationQuery = useResource({
    resource: "notifications",
    /** @todo enable pagination here */
    disablePagination: true,
    queryDefaults: {
      retryDelay: 0,
      enabled: false,
    },
  });
  const notifications = notificationQuery.data || [];

  /**
   * Write through the key the query actually observes, not the literal
   * 'notifications'. `useResource` appends search (and division/page) parts to
   * the key, so an app first loaded on a URL carrying `?search=` observes
   * `notifications_<term>` — and a write to the bare name would land on an
   * entry nothing is reading, silently discarding the update.
   */
  const setNotifications = (data) =>
    client.setQueryData(notificationQuery.key, data || []);

  const updateTypeSettings = (typeId, isEnabled, defaultMessage) => {
    if (!loading) {
      setLoading(true);

      notificationApi
        .updateTypeSettings(userId, typeId, isEnabled, defaultMessage)
        .then(data => {
          appStore.set("user.notificationSettings", data);
          setNotificationSettings(data);
          setLoading(false);
        });
    }
  };

  const updateFrequencySettings = frequency => {
    if (loading) {
      setLoading(true);

      notificationApi.updateFrequencySettings(frequency).then(data => {
        setFrequencySettings(data);
        setLoading(false);
      });
    }
  };

  /**
   * Marks one notification read, or all of them when no id is given.
   *
   * Applied to the cache immediately rather than waiting for the response. The
   * server replies with the whole list, and rebuilding that list costs a handful
   * of queries per notification — none of which tells us anything we do not
   * already know about a row being read. This also means a click that navigates
   * away is not racing a slow response.
   */
  const markNotificationAsRead = (id = null) => {
    const current = notificationQuery.data || [];

    setNotifications(
      current.map((notification) =>
        !id || notification.id === id
          ? { ...notification, is_opened: true }
          : notification,
      ),
    );

    return (id ? notificationApi.read(id) : notificationApi.readAll()).catch(
      (error) => {
        /** Put the list back in step with the server before surfacing the failure */
        notificationQuery.query.refetch();

        return Promise.reject(error);
      },
    );
  };

  /**
   * Clears every in-app notification for this user, in every tenant they have
   * access to. Irreversible from the UI, so callers confirm first.
   */
  const clearAllNotifications = () => {
    setNotifications([]);

    return notificationApi
      .clearAll()
      .catch((error) => {
        /** Put the list back in step with the server before surfacing the failure */
        notificationQuery.query.refetch();

        return Promise.reject(error);
      });
  };

  useEffect(() => {
    if (loading && !notificationQuery.loading) {
      setLoading(false);
    }
  }, [notificationQuery]);

  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => {
        notificationQuery.query.refetch();
      }, 2000);
    }
    return () => {
      clearTimeout(timer);
      notificationQuery.query.remove();
    };
  }, []);

  return (
    <Context.Provider
      value={{
        notificationSettings,
        frequencySettings,
        updateTypeSettings,
        updateFrequencySettings,
        notifications,
        markNotificationAsRead,
        clearAllNotifications,
        loading,
        loaded: !loading,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
